import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTutorial } from "../context/TutorialContext";
import { colors, shadows, radius } from "../utils/designTokens";
import {
  FaArrowRight,
  FaArrowLeft,
  FaTimes,
  FaLightbulb,
  FaCheckCircle,
  FaListOl,
  FaRocket,
  FaMousePointer,
  FaInfoCircle,
  FaQuestion,
} from "react-icons/fa";

// Renderiza texto reemplazando "(?)" por un mini boton inline
const renderDescription = (text) => {
  if (!text.includes("(?)")) return text;
  // Separar por el marcador (?)
  const segments = text.split("(?)");
  return segments.map((segment, i) => (
    <React.Fragment key={i}>
      {segment}
      {i < segments.length - 1 && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: colors.primary,
            color: colors.white,
            fontSize: 10,
            lineHeight: 1,
            verticalAlign: "middle",
            margin: "0 2px",
          }}
        >
          <FaQuestion size={9} />
        </span>
      )}
    </React.Fragment>
  ));
};

// Iconos segun tipo de paso
const STEP_ICONS = {
  welcome: FaRocket,
  checklist: FaListOl,
  ready: FaCheckCircle,
  action: FaMousePointer,
  info: FaInfoCircle,
  default: FaLightbulb,
};

/**
 * Overlay del tutorial con spotlight no-bloqueante.
 *
 * Usa box-shadow para oscurecer alrededor del target sin bloquear scroll.
 * Solo el tooltip tiene pointer-events: auto.
 */
const TutorialOverlay = () => {
  const { activeTutorial, nextStep, prevStep, skipTutorial } = useTutorial();
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const tooltipRef = useRef(null);
  const rafRef = useRef(null);

  const step = activeTutorial
    ? activeTutorial.steps[activeTutorial.currentStep]
    : null;
  const totalSteps = activeTutorial ? activeTutorial.steps.length : 0;
  const currentIdx = activeTutorial ? activeTutorial.currentStep : 0;
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === totalSteps - 1;

  // Buscar target y calcular rect
  const findAndMeasureTarget = useCallback(() => {
    if (!step || !step.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const pad = 6;
    setTargetRect({
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    });
  }, [step]);

  // ScrollIntoView del target al cambiar de paso
  useEffect(() => {
    if (!step || !step.target) {
      setTargetRect(null);
      setTransitioning(false);
      return;
    }
    setTransitioning(true);
    const el = document.querySelector(step.target);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      // Esperar a que termine el scroll para medir la posicion final
      const timer = setTimeout(() => {
        findAndMeasureTarget();
        setTransitioning(false);
      }, 350);
      return () => clearTimeout(timer);
    } else {
      setTargetRect(null);
      setTransitioning(false);
    }
  }, [step, findAndMeasureTarget]);

  // Actualizar posicion en resize y scroll
  useEffect(() => {
    const update = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(findAndMeasureTarget);
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [findAndMeasureTarget]);

  // Animar entrada/salida
  useEffect(() => {
    if (activeTutorial) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [activeTutorial]);

  // Atajos de teclado
  useEffect(() => {
    if (!activeTutorial) return;
    const handleKey = (e) => {
      if (e.key === "Escape") skipTutorial();
      if (e.key === "ArrowRight" || e.key === "Enter") nextStep();
      if (e.key === "ArrowLeft") prevStep();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeTutorial, nextStep, prevStep, skipTutorial]);

  if (!activeTutorial || !step) return null;

  const IconComp = STEP_ICONS[step.icon] || STEP_ICONS.default;
  const isModal = !step.target || !targetRect;

  // Calcular posicion del tooltip
  const computeTooltipStyle = () => {
    if (isModal) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 100002,
        pointerEvents: "auto",
      };
    }

    const pos = step.position || "right";
    const gap = 16;
    const style = {
      position: "fixed",
      zIndex: 100002,
      pointerEvents: "auto",
    };

    if (pos === "right") {
      style.top = targetRect.top + targetRect.height / 2;
      style.left = targetRect.left + targetRect.width + gap;
      style.transform = "translateY(-50%)";
      // Si se sale por la derecha, mover abajo
      if (style.left + 380 > window.innerWidth) {
        style.left = undefined;
        style.right = 16;
        style.transform = "translateY(-50%)";
      }
    } else if (pos === "bottom") {
      style.top = targetRect.top + targetRect.height + gap;
      style.left = targetRect.left + targetRect.width / 2;
      style.transform = "translateX(-50%)";
    } else if (pos === "left") {
      style.top = targetRect.top + targetRect.height / 2;
      style.right = window.innerWidth - targetRect.left + gap;
      style.transform = "translateY(-50%)";
    } else if (pos === "top") {
      style.bottom = window.innerHeight - targetRect.top + gap;
      style.left = targetRect.left + targetRect.width / 2;
      style.transform = "translateX(-50%)";
    }

    // Clamp vertical
    if (typeof style.top === "number") {
      style.top = Math.max(16, Math.min(style.top, window.innerHeight - 300));
    }
    // Clamp horizontal
    if (typeof style.left === "number") {
      style.left = Math.max(16, Math.min(style.left, window.innerWidth - 396));
    }

    return style;
  };

  const tooltipStyle = computeTooltipStyle();

  return (
    <>
      {/* Capa: backdrop para modales (bloquea clicks), invisible para spotlight (no bloquea) */}
      {isModal ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            background: "rgba(0, 0, 0, 0.55)",
            opacity: visible ? 1 : 0,
            transition: "opacity 0.25s ease",
            pointerEvents: "auto",
          }}
          onClick={skipTutorial}
        />
      ) : (
        /* Spotlight: div posicionado sobre el target con box-shadow gigante */
        !transitioning && targetRect && (
          <div
            style={{
              position: "fixed",
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
              zIndex: 100000,
              borderRadius: radius.sm,
              boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 2px ${colors.primary}, 0 0 12px 2px rgba(0, 128, 255, 0.25)`,
              pointerEvents: "none",
              opacity: visible ? 1 : 0,
              transition: "all 0.3s ease",
            }}
          />
        )
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        style={{
          ...tooltipStyle,
          width: isModal ? 460 : 380,
          maxWidth: "calc(100vw - 32px)",
          background: colors.white,
          borderRadius: radius.md,
          boxShadow: shadows.modal,
          padding: 0,
          fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
          opacity: visible && !transitioning ? 1 : 0,
          transition: "opacity 0.25s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px 12px",
            borderBottom: `1px solid ${colors.gray200}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: radius.sm,
                background: colors.primaryLighter,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconComp size={16} color={colors.primary} />
            </div>
            <span style={{ fontSize: 15, fontWeight: 400, color: colors.black }}>
              {step.title}
            </span>
          </div>
          <button
            onClick={skipTutorial}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              borderRadius: radius.sm,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.gray400,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.gray700)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.gray400)}
            title="Cerrar tutorial (Esc)"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            padding: "16px 20px",
            fontSize: 13,
            lineHeight: 1.7,
            color: colors.gray700,
            whiteSpace: "pre-line",
            fontWeight: 400,
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {renderDescription(step.description)}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 20px 16px",
            borderTop: `1px solid ${colors.gray100}`,
          }}
        >
          {/* Progreso */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === currentIdx ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === currentIdx ? colors.primary : colors.gray200,
                  transition: "all 0.2s ease",
                }}
              />
            ))}
            <span
              style={{ fontSize: 11, color: colors.gray400, marginLeft: 8, fontWeight: 400 }}
            >
              {currentIdx + 1} / {totalSteps}
            </span>
          </div>

          {/* Botones */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isFirst ? (
              <button onClick={skipTutorial} style={btnSecondaryStyle}>
                Omitir
              </button>
            ) : (
              <button onClick={prevStep} style={btnSecondaryStyle}>
                <FaArrowLeft size={11} /> Anterior
              </button>
            )}
            <button
              onClick={nextStep}
              style={btnPrimaryStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = colors.primaryDark)}
              onMouseLeave={(e) => (e.currentTarget.style.background = colors.primary)}
            >
              {isLast ? "Finalizar" : "Siguiente"}
              {isLast ? <FaCheckCircle size={11} /> : <FaArrowRight size={11} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const btnPrimaryStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 16px",
  borderRadius: radius.sm,
  border: "none",
  background: colors.primary,
  color: colors.white,
  fontSize: 13,
  fontWeight: 400,
  cursor: "pointer",
  transition: "all 0.15s",
};

const btnSecondaryStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: radius.sm,
  border: `1px solid ${colors.gray200}`,
  background: colors.white,
  color: colors.gray600,
  fontSize: 13,
  fontWeight: 400,
  cursor: "pointer",
};

export default TutorialOverlay;
